// Third-party
import React from 'react';
import { withRouter, RouteComponentProps, Route, Redirect, Switch } from 'react-router';
import { formatMessage } from '../../../../utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../../utils/permissions';

// Components
import EnterpriseMembers from './enterprise-members/EnterpriseMembers';
import EnterprisePortfolios from './EnterprisePortfolios';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import User from '@/types/user';
import AppRegion from '@/types/app-region';
import EnterpriseInfos from '@/types/enterprise-infos';
import EnterpriseDetailInfo from '@/types/enterprise-detail-info';
import NavigationItem from '@/types/navigation-item';

// Legacy
import LegacyAdminEnterprise from './LegacyAdminEnterprise';

enum ADMIN_ENTERPRISE_TABS {
  MEMBERS = 'members',
  MODELS = 'models',
}

type Props = RouteComponentProps & {
  appRegion: AppRegion;
  enterprise: EnterpriseDetailInfo;
  pills?: NavigationItem[];
  user: User;
  onSave: (info: EnterpriseInfos) => Promise<EnterpriseInfos>;
};

const AdminEnterprise = ({ enterprise, pills, user, onSave }: Props) => {
  if (!enterprise) {
    return null;
  }

  const memberAdmin = userHasPermissionEnabled(
    user,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
  );
  const modelAdmin = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS);
  const redirect = memberAdmin ? '/admin/enterprise/members' : '/admin/enterprise/models';

  const i18nBasePath = 'Admin.Enterprise';
  const navBasePath = '/admin/enterprise/';

  const membersNavPath = `${navBasePath}${ADMIN_ENTERPRISE_TABS.MEMBERS}`;
  const modelsNavPath = `${navBasePath}${ADMIN_ENTERPRISE_TABS.MODELS}`;

  const getSelectedAdminEnterpriseTab = () => {
    switch (window.location.pathname) {
      case membersNavPath: {
        return ADMIN_ENTERPRISE_TABS.MEMBERS;
      }
      case modelsNavPath: {
        return ADMIN_ENTERPRISE_TABS.MODELS;
      }
      default: {
        return ADMIN_ENTERPRISE_TABS.MEMBERS;
      }
    }
  };

  const getAdminEnterpriseTabsOptions = () => [
    {
      id: ADMIN_ENTERPRISE_TABS.MEMBERS,
      value: membersNavPath,
      label: formatMessage(`${i18nBasePath}.EnterpriseMembers`),
      i18nPath: `${i18nBasePath}.EnterpriseMembers`,
      disabled: !memberAdmin,
    },
    {
      id: ADMIN_ENTERPRISE_TABS.MODELS,
      value: modelsNavPath,
      label: formatMessage(`${i18nBasePath}.PortfoliosTitle`),
      i18nPath: `${i18nBasePath}.PortfoliosTitle`,
      disabled: !modelAdmin,
    },
  ];

  return (
    <div className="admin-enterprise">
      <Switch>
        <Route path="/admin/enterprise/members">
          <EnterpriseMembers
            adminEnterpriseTabOptions={getAdminEnterpriseTabsOptions()}
            enterprise={enterprise}
            selectedAdminEnterpriseTab={getSelectedAdminEnterpriseTab()}
            pills={pills}
            onSave={onSave}
          />
        </Route>

        <Route path="/admin/enterprise/models">
          <EnterprisePortfolios
            adminEnterpriseTabOptions={getAdminEnterpriseTabsOptions()}
            enterprise={enterprise}
            pills={pills}
            selectedAdminEnterpriseTab={getSelectedAdminEnterpriseTab()}
            user={user}
            onSave={onSave}
          />
        </Route>

        <Redirect to={redirect} />
      </Switch>
    </div>
  );
};

const AdminEnterpriseComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <AdminEnterprise {...props} />;
  }
  return <LegacyAdminEnterprise {...props} />;
};

export default withRouter(AdminEnterpriseComponent);
